import { useState, useRef, useEffect, memo } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { cn } from '../../utils/cn'
import { useAuthStore } from '../../stores/authStore'
import { useFamilyStore } from '../../stores/familyStore'
import { useUserPreferencesStore } from '../../stores/userPreferencesStore'
import { getColorScheme } from '../../utils/colorSchemes'
import { clearUserRoleCache } from '../../utils/accessControl'
import { Settings, LogOut, Home, Info, Mail, CreditCard, User } from 'lucide-react'
import { useEntitlements } from '../../hooks/useEntitlements'
import logger from '../../utils/logger'

interface NavbarProps {
  currentHome?: "home_a" | "home_b"
  onHomeChange?: (home: "home_a" | "home_b") => void
  selectedChild?: string
  onChildChange?: (childId: string) => void
  children?: any[]
  isLoading?: boolean
  currentView?: "care" | "events"
  onViewChange?: (view: "care" | "events") => void
  onAddEventClick?: () => void
}

const Navbar = memo<NavbarProps>(({ 
  currentHome = "home_a", 
  onHomeChange,
  selectedChild,
  onChildChange,
  children,
  isLoading,
  currentView = "care",
  onViewChange,
  onAddEventClick
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const user = useAuthStore(state => state.user)
  const signOut = useAuthStore(state => state.signOut)
  const clearCache = useFamilyStore(state => state.clearCache)
  const family = useFamilyStore(state => state.family)
  const scheme = useUserPreferencesStore(state => getColorScheme(state.preferences.color_scheme))
  const [showUserMenu, setShowUserMenu] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const { planId } = useEntitlements(family?.id)
  
  // Pobierz aktualne kolory schematu i nazwy domów
  const homeAName = family?.home_a_name || 'Dom A'
  const homeBName = family?.home_b_name || 'Dom B'
  
  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSignOut = async () => {
    setShowUserMenu(false)
    try {
      logger.debug('🚪 Rozpoczynam proces wylogowania z Navbar...')
      
      // Wyczyść cache roli użytkownika i dane rodziny
      clearUserRoleCache()
      clearCache()
      
      // Wywołaj funkcję wylogowania z authStore
      // Ta funkcja zajmie się czyszczeniem sesji, localStorage, sessionStorage
      // oraz przekierowaniem na stronę logowania
      await signOut()
      
      // Nie potrzebujemy już przekierowania tutaj, ponieważ jest obsługiwane w authStore
    } catch (error) {
      logger.error('❌ Błąd wylogowania z Navbar:', error)
      // W przypadku błędu, spróbujmy wymusić przekierowanie
      window.location.href = '/auth/login'
    }
  }
  
  // Funkcja do generowania inicjałów użytkownika
  const getUserInitials = () => {
    if (!user || !user.email) return '?'
    
    const email = user.email
    const nameParts = email.split('@')[0].split('.')
    
    if (nameParts.length >= 2) {
      return (nameParts[0][0] + nameParts[1][0]).toUpperCase()
    }
    
    return email.substring(0, 2).toUpperCase()
  }

  // Sprawdź czy jesteśmy na stronie kalendarza
  const isCalendarPage = location.pathname === '/calendar'
  
  return (
    <nav className="bg-white shadow sticky top-0 z-50">
      {/* Górny rząd - Logo, przełącznik domu, avatar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo z gradientem */}
          <div className="flex-shrink-0 flex items-center">
            <Link to={user ? "/calendar" : "/"} className="flex items-center" onClick={() => {
              if (onViewChange) {
                onViewChange('care')
              }
            }}>
              <span className="font-bold text-xl bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 bg-clip-text text-transparent">
                2domy.pl
              </span>
            </Link>
          </div>

          {/* Środkowa sekcja - Przełącznik domu (tylko na stronie kalendarza i tylko w widoku Opieka) */}
          {isCalendarPage && onHomeChange && currentView !== 'events' && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onHomeChange("home_a")}
                className={cn(
                  "flex items-center space-x-1 px-3 py-2 rounded-lg text-xs font-medium",
                  "cursor-pointer select-none touch-manipulation",
                  "transition-all duration-100 active:scale-95",
                  "text-white border",
                  currentHome === "home_a" 
                    ? "shadow-md border-2 ring-2 ring-blue-900" 
                    : "opacity-60 hover:opacity-80 border-white/30"
                )}
                style={{
                  backgroundColor: scheme.homeA.color,
                  borderColor: currentHome === "home_a" ? scheme.homeA.color : undefined
                }}
              >
                <Home className="h-3 w-3" />
                <span className="truncate max-w-16">{homeAName}</span>
              </button>
              <button
                onClick={() => onHomeChange("home_b")}
                className={cn(
                  "flex items-center space-x-1 px-3 py-2 rounded-lg text-xs font-medium",
                  "cursor-pointer select-none touch-manipulation",
                  "transition-all duration-100 active:scale-95",
                  "text-white border",
                  currentHome === "home_b" 
                    ? "shadow-md border-2 ring-2 ring-blue-900" 
                    : "opacity-60 hover:opacity-80 border-white/30"
                )}
                style={{
                  backgroundColor: scheme.homeB.color,
                  borderColor: currentHome === "home_b" ? scheme.homeB.color : undefined
                }}
              >
                <Home className="h-3 w-3" />
                <span className="truncate max-w-16">{homeBName}</span>
              </button>
            </div>
          )}

          {/* Avatar użytkownika + badge planu (na avatarze) */}
          <div className="flex items-center gap-4">
            {user && (
              <div className="flex items-center">
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium shadow-sm hover:shadow-md transition-all duration-300"
                  >
                    {getUserInitials()}
                  </button>
                  {planId && (
                    <span
                      title="Twój plan"
                      className={cn(
                        'absolute -bottom-1 -right-1 text-[10px] leading-none px-1.5 py-0.5 rounded-full border shadow ring-2 ring-white',
                        // Wyraziste, kontrastowe kolory względem niebieskiego avatara
                        planId === 'max' && 'bg-amber-500 text-white border-amber-600',
                        planId === 'pro' && 'bg-emerald-600 text-white border-emerald-700',
                        planId === 'basic' && 'bg-slate-500 text-white border-slate-600'
                      )}
                    >
                      {planId === 'max' ? 'Max' : planId === 'pro' ? 'Pro' : 'Basic'}
                    </span>
                  )}
                  {/* User Dropdown */}
                  {showUserMenu && (
                    <div className="absolute right-0 top-10 w-48 bg-slate-900/95 text-white rounded-lg shadow-2xl border border-slate-800 ring-1 ring-black/20 backdrop-blur-sm z-50">
                      <div className="p-3 border-b border-slate-700/60 bg-white/5 rounded-t-lg">
                        <p className="text-sm font-medium text-slate-100">{user?.email}</p>
                      </div>
                      <div className="p-2 space-y-1">
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            navigate('/family-settings');
                          }}
                          className="w-full flex items-center px-3 py-2 text-sm text-slate-200 hover:text-white hover:bg-white/10 rounded-md"
                        >
                          <Settings className="h-4 w-4 mr-2" />
                          Ustawienia
                        </button>
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            navigate('/account');
                          }}
                          className="w-full flex items-center px-3 py-2 text-sm text-slate-200 hover:text-white hover:bg-white/10 rounded-md"
                        >
                          <User className="h-4 w-4 mr-2" />
                          Konto
                        </button>
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            navigate('/pricing');
                          }}
                          className="w-full flex items-center px-3 py-2 text-sm text-slate-200 hover:text-white hover:bg-white/10 rounded-md"
                        >
                          <CreditCard className="h-4 w-4 mr-2" />
                          Plan i cennik
                        </button>
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            navigate('/contact');
                          }}
                          className="w-full flex items-center px-3 py-2 text-sm text-slate-200 hover:text-white hover:bg-white/10 rounded-md"
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          Kontakt
                        </button>
                        <button
                          onClick={handleSignOut}
                          className="w-full flex items-center px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-md"
                        >
                          <LogOut className="h-4 w-4 mr-2" />
                          Wyloguj się
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dolny rząd - Wybór dziecka i menu widoku (tylko na stronie kalendarza) */}
      {isCalendarPage && (
        <div className="border-t border-gray-200 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-2">
              {/* Wybór dziecka po lewej – zawsze widoczny, interakcja blokowana tylko podczas ładowania */}
              {children && onChildChange && (
                <div className="flex items-center space-x-2">
                  <select
                    value={selectedChild || ""}
                    onChange={(e) => onChildChange(e.target.value)}
                    className="px-3 py-1 text-sm rounded-lg bg-blue-50 text-blue-800 border border-blue-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={isLoading}
                  >
                    {children.map((child) => (
                      <option key={child.id} value={child.id}>
                        {child.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Pojedynczy przycisk Dodaj zamiast przełącznika widoków */}
              <div className="flex space-x-2 items-center">
                <button
                  type="button"
                  aria-label="Instrukcja"
                  title="Instrukcja"
                  onClick={() => (window as any).__openUsageInfo?.()}
                  className="p-0 m-0 bg-transparent border-0 text-sky-600 hover:text-sky-700 focus:outline-none focus-visible:outline-none"
                >
                  <Info className="h-8 w-8" />
                </button>
                <button
                  onClick={() => onAddEventClick?.()}
                  className={cn(
                    "w-28 px-4 py-2 text-sm font-medium rounded-lg transition-all inline-flex items-center justify-center text-center border-2 text-white shadow-md ring-2 ring-blue-900",
                  )}
                  style={{
                    backgroundImage: `linear-gradient(to bottom right, ${scheme.homeA.color}, ${scheme.homeB.color})`,
                    borderColor: scheme.homeA.color
                  }}
                >
                  Dodaj
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  )
})

export default Navbar 